import React, { useEffect, useState } from "react";
import Logo from "../assets/img/logo.png";
import UserImg from "../assets/img/User_img.png";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import {
  ShoppingCartIcon,
  UserIcon,
  ArrowRightEndOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import CartService from "../Services/CartService";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import EmptyCartModal from "../App/Account/EmptyCartModal";
import shippingStore from "../stores/ShippingStore";

interface HeaderProps {
  title: string;
  links: { name: string; href: string }[];
  productName?: string;
  price?: number;
  onAddToCart?: () => void;
  cartItemsCount: number;
  onPreview?: () => void;
  showPreviewButton?: boolean;
  onShowSuccess?: boolean;
  onSaveButton?: boolean;
  handleSaveButton?: () => void;
  canvasName?: string;
}


const S = {
  header: {
    height: 64,
    background: '#13151f',
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 16px',
    gap: 12,
    position: 'relative' as const,
    zIndex: 50,
    userSelect: 'none' as const,
  },
  logoWrap: { display: 'flex', alignItems: 'center', gap: 10, marginRight: 4, flexShrink: 0 },
  logoMark: {
    width: 32, height: 32,
    background: 'linear-gradient(135deg,#5b79ff,#8b5cf6)',
    borderRadius: 8,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 900, color: '#fff', letterSpacing: -1,
  },
  logoText: { fontSize: 14, fontWeight: 700, color: '#f0f2ff', letterSpacing: 0.2 },
  divider: { width: 1, height: 28, background: 'rgba(255,255,255,0.07)', flexShrink: 0 },
  projectName: {
    fontSize: 13, fontWeight: 500, color: '#9da3c8',
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '4px 8px', borderRadius: 6,
  },
  projectTitle: { fontSize: 13, fontWeight: 600, color: '#f0f2ff' },
  spacer: { flex: 1 },
  right: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  saveBadge: {
    display: 'flex', alignItems: 'center', gap: 5,
    fontSize: 11, color: '#25d07a',
    padding: '4px 10px',
    background: 'rgba(37,208,122,0.12)',
    border: '1px solid rgba(37,208,122,0.25)',
    borderRadius: 20,
  },
  saveDot: { width: 6, height: 6, borderRadius: '50%', background: '#25d07a', flexShrink: 0 },
  btnGhost: {
    padding: '6px 12px', borderRadius: 8,
    fontSize: 12, fontWeight: 500,
    color: '#9da3c8',
    background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
    transition: 'all 150ms',
  },
  btnSave: {
    padding: '7px 16px', borderRadius: 8,
    fontSize: 12, fontWeight: 600, color: '#fff',
    background: '#5b79ff', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  btnCart: {
    padding: '7px 16px', borderRadius: 8,
    fontSize: 12, fontWeight: 600, color: '#fff',
    background: '#25d07a', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  cartWrap: { position: 'relative' as const },
  cartDot: {
    position: 'absolute' as const, top: -4, right: -4,
    minWidth: 18, height: 18,
    background: '#ff5b6b', borderRadius: 9,
    fontSize: 9, fontWeight: 700, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 4px',
    pointerEvents: 'none' as const,
  },
  iconBtn: {
    width: 34, height: 34, borderRadius: 8,
    background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#9da3c8',
  },
  userMenuTrigger: {
    display: 'flex', alignItems: 'center', gap: 8,
    cursor: 'pointer', background: 'none', border: 'none',
    padding: '4px 8px', borderRadius: 8,
  },
  userAvatar: {
    width: 30, height: 30, borderRadius: '50%',
    border: '2px solid rgba(91,121,255,0.4)',
    objectFit: 'cover' as const,
  },
  userName: { fontSize: 12, fontWeight: 500, color: '#9da3c8' },
  dropdown: {
    position: 'absolute' as const, right: 0, top: 'calc(100% + 8px)',
    minWidth: 200, zIndex: 100,
    background: '#1c1e2e', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '11px 16px',
    fontSize: 13, color: '#9da3c8',
    cursor: 'pointer', background: 'none', border: 'none', width: '100%',
    transition: 'all 150ms',
  },
};

const Header: React.FC<HeaderProps> = ({
  title: _title,
  links: _links,
  productName,
  price,
  onAddToCart,
  cartItemsCount,
  onPreview,
  showPreviewButton,
  onShowSuccess,
  onSaveButton,
  handleSaveButton,
  canvasName,
}) => {
  const [cartItemCount, setCartItemCount] = useState(0);
  const navigate = useNavigate();
  const [isEmptyCartModalOpen, setIsEmptyCartModalOpen] = useState(false);

  useEffect(() => {
    setCartItemCount(cartItemsCount);
  }, [cartItemsCount]);

  useEffect(() => {
    CartService.GetCartCount()
      .then(({ CartCount }) => setCartItemCount(CartCount))
      .catch(console.error);
  }, []);

  const ShowYourCart = async () => {
    try {
      const { CartCount } = await CartService.GetCartCount();
      setCartItemCount(CartCount);
      if (CartCount === 0) { setIsEmptyCartModalOpen(true); }
      else { navigate('/yourcart', { replace: false }); }
    } catch { setIsEmptyCartModalOpen(true); }
  };

  const { user, signout } = useAuth();
  const LogOut = async () => {
    await signout();
    window.localStorage.clear();
    shippingStore.clearShippingDetails();
    navigate(process.env.REACT_APP_PublicPath + "/login");
  };

  const displayName = canvasName || productName || _title;

  return (
    <>
      <div style={S.header}>
        {/* Logo */}
        <div style={S.logoWrap}>
          <img src={Logo} style={{ height: 32, width: 'auto' }} alt="Logo" />
        </div>

        <div style={S.divider} />

        {/* Project name */}
        <div style={S.projectName}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          </svg>
          <a href="/" style={{ color: '#5c6290', fontSize: 12, textDecoration: 'none' }}>Home</a>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.4 }}><polyline points="9 18 15 12 9 6"/></svg>
          {displayName && <span style={S.projectTitle}>{displayName}</span>}
        </div>

        <div style={S.spacer} />

        {/* Right actions */}
        <div style={S.right}>
          {/* Auto-save badge */}
          {onShowSuccess && (
            <div style={S.saveBadge}>
              <div style={S.saveDot} />
              Saved
            </div>
          )}

          {/* Preview */}
          {showPreviewButton && (
            <button style={S.btnGhost} onClick={onPreview}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview
            </button>
          )}

          {/* Save & Close */}
          {onSaveButton && (
            <button style={S.btnSave} onClick={handleSaveButton}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              Save &amp; Close
            </button>
          )}

          {/* Add to Cart */}
          {onSaveButton && (
            <button style={S.btnCart} onClick={onAddToCart}>
              Add to Cart{price !== undefined ? ` · $${price.toFixed(2)}` : ''}
            </button>
          )}

          <div style={S.divider} />

          {/* Cart icon */}
          <div style={S.cartWrap}>
            <button style={S.iconBtn} onClick={ShowYourCart} title="Your cart">
              <ShoppingCartIcon style={{ width: 18, height: 18 }} />
            </button>
            {cartItemCount > 0 && <div style={S.cartDot}>{cartItemCount}</div>}
          </div>

          <div style={S.divider} />

          {/* User menu */}
          <Menu as="div" style={{ position: 'relative' }}>
            <MenuButton style={S.userMenuTrigger as any}>
              <img src={UserImg} style={S.userAvatar} alt="User" />
              <span style={S.userName}>{user?.firstName} {user?.lastName}</span>
              <ChevronDownIcon style={{ width: 14, height: 14, color: '#5c6290' }} />
            </MenuButton>
            <MenuItems style={S.dropdown}>
              <div style={{ padding: '8px 0' }}>
                <MenuItem>
                  <button
                    style={S.dropdownItem}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(91,121,255,0.1)'; e.currentTarget.style.color = '#f0f2ff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9da3c8'; }}
                    onClick={() => navigate('/myaccounts')}
                  >
                    <UserIcon style={{ width: 16, height: 16 }} />
                    My Account
                  </button>
                </MenuItem>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />
                <MenuItem>
                  <button
                    style={S.dropdownItem}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,91,107,0.1)'; e.currentTarget.style.color = '#ff5b6b'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#9da3c8'; }}
                    onClick={LogOut}
                  >
                    <ArrowRightEndOnRectangleIcon style={{ width: 16, height: 16 }} />
                    Log Out
                  </button>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
      </div>

      <EmptyCartModal isOpen={isEmptyCartModalOpen} onClose={() => setIsEmptyCartModalOpen(false)} />
    </>
  );
};

export default Header;
